import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import Header from '../../../components/header/Header';

import icSimpleTicket from '../../../assets/imgs/ic_simple_ticket.svg';
import icSuccessCheck from '../../../assets/imgs/ic_coupon_check.svg';
import icMailBox from '../../../assets/imgs/mailbox.png';

import styles from './styles.module.css';
import useDirectUseTicket from '../../../hooks/useDirectUseTicket';
import CenterLoading from '../../../components/CenterLoading/CenterLoading';
import useUsageSpotTicket from '../../../hooks/useUsageSpotTicket';
import { DISTRIBUTION_TYPE, localStorageKeys, PRICE_TYPE, QR_TYPE, TICKET_TYPE } from '../../../utils/Constants';
import useActivateTicket from '../../../hooks/useActivateTicket';
import TextCopy from '../../../components/TextCopy';
import CustomAntButton from '../../../components/CustomAntButton/CustomAntButton';
import useQrCode from '../../../hooks/useDisplayQrCode';
import { isReactNativeWebView, sendMessageToNativeApp } from '../../../utils/reactNativeUtils';

/**
 * 図2-2 チケット購入完了画面
 */
const BuyTicketSuccessScreen = memo(() => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const location = useLocation();

    const { isLoading, orderTicket, canUseDirect, fetchOrder } = useDirectUseTicket();
    const { loading: isLoadingSpotUsage, checkUsageSpotTicket } = useUsageSpotTicket();
    const { loading: isLoadingActivate, ActivateConfirmModal, checkActivateTicket } = useActivateTicket();
    const { fetchQrCode, isLoading: isQrCodeLoading } = useQrCode();

    const orderId = location?.state?.orderId;
    const { appHomepageUrl } = JSON.parse(localStorage.getItem('APP_CONFIG') ?? '{}');

    useEffect(() => {
        void fetchOrder(orderId);
    }, [orderId, fetchOrder]);

    useEffect(() => {
        if (orderTicket?.qrType === QR_TYPE.SINGLE_SCAN) {
            const { remainingOrderDistribution, orderTicketId, distributionType } = orderTicket ?? {};
            const { remainingOrderDistributionId } = remainingOrderDistribution ?? {};

            let requestParams: {
                remainingOrderDistributionId?: string | null;
                orderTicketId?: string | null;
            } = { remainingOrderDistributionId };
            if (distributionType === DISTRIBUTION_TYPE.DISABLED) {
                requestParams = { orderTicketId };
            }
            void fetchQrCode(requestParams);
        }
    }, [orderTicket]);

    useEffect(() => {
        localStorage.removeItem(localStorageKeys.TICKET_INFO_DATA);
        localStorage.removeItem(localStorageKeys.RESERVATION_INFO_DATA);
        localStorage.removeItem(localStorageKeys.ANONYMOUS_FORM_ANSWERS);
        localStorage.removeItem(localStorageKeys.RESERVATION_FLOW);
    }, []);

    /**
     * 直接使用
     */
    const handleUseDirectClick = useCallback(() => {
        if (!orderTicket) {
            return;
        }
        const { ticketType } = orderTicket;
        if (ticketType === TICKET_TYPE.TICKET) {
            void checkActivateTicket(orderTicket);
            return;
        }
        if (ticketType === TICKET_TYPE.COUPON) {
            void checkUsageSpotTicket(orderTicket, true);
            return;
        }
    }, [orderTicket, checkActivateTicket, checkUsageSpotTicket]);

    /**
     * チケットをあとで使うボタンを押すの処理
     */
    const handleTicketUsageLaterClick = () => {
        navigate('/bought');
    };

    const handleHomepageClick = () => {
        window.open(appHomepageUrl, '_self');
    };

    const handleDistributionsClick = async () => {
        if (!orderTicket) {
            return;
        }
        if (isReactNativeWebView()) {
            // ネイティブアプリにメッセージを送信して、ネイティブアプリで画面遷移を行う。
            sendMessageToNativeApp('purchase_complete', {
                orderId: orderId,
            });

            return;
        }
        const orderTicketId = orderTicket.orderTicketId;
        if (orderTicketId) {
            navigate('/bought/distributions/info', { state: { orderTicketId } });
        }
    };

    return (
        <>
            {(isLoading || isLoadingActivate || isLoadingSpotUsage || isQrCodeLoading) && <CenterLoading />}
            <ActivateConfirmModal />
            <Header title={t('header.buy-ticket')} />
            <div className={`content-container scrollable`}>
                {!isLoading ? (
                    <div className={styles.wrapper}>
                        <img src={icSuccessCheck} className={styles.successIcon} alt="" />
                        <p className={styles.title}>{t('ticket-buy-success.header-message')}</p>
                        <img src={icMailBox} className={styles.mailIcon} alt="" />
                        <p className={styles.info}>
                            {orderTicket?.priceType === PRICE_TYPE.NO_COST
                                ? t('ticket-buy-success.send-notification-message')
                                : t('ticket-buy-success.send-receipt-message')}
                        </p>
                        <img src={icSimpleTicket} className={styles.ticketIcon} alt="" />
                        <p className={styles.info} style={{ marginTop: 7, opacity: 0.6 }}>
                            {t('ticket-buy-success.invoice-label')}
                        </p>
                        <div className={styles.info}>
                            <TextCopy>{orderId}</TextCopy>
                        </div>
                        <div className={styles.actions}>
                            {canUseDirect && orderTicket?.distributionType !== DISTRIBUTION_TYPE.REQUIRED && (
                                <>
                                    <CustomAntButton className={styles.actionBtn} block onClick={handleUseDirectClick}>
                                        {t('ticket-buy-success.use-directly')}
                                    </CustomAntButton>
                                    <CustomAntButton
                                        type="outlined"
                                        className={styles.actionBtn}
                                        block
                                        onClick={handleTicketUsageLaterClick}
                                    >
                                        {t('ticket-buy-success.go-to-history')}
                                    </CustomAntButton>
                                </>
                            )}
                            {!canUseDirect && orderTicket?.distributionType !== DISTRIBUTION_TYPE.REQUIRED && (
                                <CustomAntButton
                                    type="outlined"
                                    className={styles.actionBtn}
                                    block
                                    onClick={handleTicketUsageLaterClick}
                                >
                                    {t('button.activate-ticket')}
                                </CustomAntButton>
                            )}
                            {orderTicket?.distributionType === DISTRIBUTION_TYPE.REQUIRED && (
                                <CustomAntButton
                                    type="outlined"
                                    className={styles.actionBtn}
                                    block
                                    onClick={handleDistributionsClick}
                                >
                                    {t('button.proceed-to-next')}
                                </CustomAntButton>
                            )}
                            {appHomepageUrl && (
                                <CustomAntButton
                                    type="outlined"
                                    className={styles.actionBtn}
                                    onClick={handleHomepageClick}
                                    block
                                >
                                    {t('button.return-to-homepage')}
                                </CustomAntButton>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className={styles.loadingTitle}>{t('loading.small-bought-ticket')}</div>
                )}
            </div>
        </>
    );
});

export default BuyTicketSuccessScreen;
